import React, { ChangeEvent, useEffect, useState } from 'react';
import { CommonLayout, ConfirmationDialog } from '../commonComponents';
import { Formik, FormikHelpers, validateYupSchema, yupToFormErrors } from 'formik';
import {
  Typography,
  Box,
  Grid,
  Button,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Input,
  Checkbox,
  Pagination,
  Stack,
  Hidden,
} from '@mui/material';
import MFSelectField from '../../lib/formik/SelectField';
import { bulkMakersChange, getRMsList } from '../../redux-store/actions/userManagement';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationProps, RmsType, newRmType } from '../../redux-store/types/api-types';
import { updateApplicationRmSchema } from '../../utils/schema';
import { getAllApplications } from '../../redux-store/actions/application';
import { GetAllApplicantionsResponseBody } from '../../redux-store/types/application';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';

import DashboardAndApplicationsList from '../dashboard/applicationslist';
import { MFSubmitButton } from '../../lib/formik';
import { RootStateType } from '../../redux-store/reducers';
import { useDebounce } from '../../utils/useDebounce';
import { LoadingButton } from '@mui/lab';
import {
  getApplicationStatus,
  IncludedApplicationStatus,
  IncludedApplicationStatusFile,
} from '../../redux-store/actions';
import { useSnackbar } from 'notistack';

export interface updateApplicationValues {
  featchDataFromRm: string;
  sendDataToRm: string;
}

const checkSelectedDataLenght = (selectedData: ApplicationProps[]): number => {
  return selectedData.filter((selected) => selected.isChecked).length;
};

const updateApplicationRmValues: updateApplicationValues = {
  featchDataFromRm: '',
  sendDataToRm: '',
};

export default function updateRmApplications(): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const { page, search: storeSearch } = useSelector((store: RootStateType) => store.paramsObj);
  const [formikInitialValues, setFormikInitialValues] =
    useState<updateApplicationValues>(updateApplicationRmValues);
  const [updateRmLoader, setUpdateRmLoader] = useState(false);
  const [viewTable, setViewTable] = useState<boolean>(false);
  const [applicationStatus, setApplicationStatus] = useState<IncludedApplicationStatus[]>([]);
  const [noOfPages, setNoPages] = useState(1);
  const [featChDataFromRmId, setFeatChDataFromRmId] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(page);
  const [loader, setLoader] = useState<boolean>(false);
  const [featchDataLoader, setFetchDataLoader] = useState<boolean>(false);
  const [confirmDialog, setConfirmDialog] = useState<boolean>(false);
  const [confirmDialogForAllSelect, setConfirmDialogForAllSelect] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<ApplicationProps[]>([]);
  const [applicationListByRmId, setApplicationListByRmId] = useState<ApplicationProps[]>([]);
  const [rmsData, setRmsData] = useState<RmsType[]>([]);
  const [sendToRmData, setSendToRmsData] = useState<RmsType[]>([]);
  const [search, setSearch] = useState(storeSearch);
  const debounceSearchString = useDebounce(search, 500);
  const dispatch = useDispatch();

  const addTableData = async (featChDataFromRmId: string, currentPage: number): Promise<void> => {
    if (!featChDataFromRmId) {
      return;
    }

    try {
      setLoader(true);
      const response = (await dispatch(
        getAllApplications({
          page: currentPage,
          search,
          rmId: featChDataFromRmId,
          status: applicationStatus.map((allStatus) => allStatus.key).toString(),
        })
      )) as unknown as GetAllApplicantionsResponseBody;

      const { applications, pages } = response || {};
      setNoPages(pages);

      const updatedApplications = applications.map((application) => {
        const selectedRow = selectedRowData.find(
          (row) => row.applicationNumber === application.applicationNumber
        );
        if (selectedRow) {
          return {
            ...selectedRow,
            isChecked: selectedRow.isChecked,
          };
        }
        return application;
      });

      setApplicationListByRmId(updatedApplications);
    } catch (error) {
      console.log((error as Error).message);
    }
    setLoader(false);
  };

  const removeSelectedData = () => {
    setApplicationListByRmId((applicationData) =>
      applicationData.map((appData) => {
        return { ...appData, isChecked: false };
      })
    );
    setSelectedRowData((selectedData) =>
      selectedData.map((appData) => {
        return { ...appData, isChecked: false };
      })
    );
    setAllSelected(false);
  };
  const handlePageChange = (event: ChangeEvent<unknown>, value: number): void => {
    if (value === currentPage) {
      return;
    }
    setCurrentPage(value);
    setAllSelected(false);
    addTableData(featChDataFromRmId, value);
  };
  const addSelectedRow = (rowData: ApplicationProps[]): void => {
    const updatedSelectedData: ApplicationProps[] = applicationListByRmId.map((application) => {
      const selectedData = rowData.find(
        (data) => data.applicationNumber === application.applicationNumber
      );

      if (selectedData) {
        if (allSelected) {
          return {
            ...application,
            isChecked: selectedData.isChecked,
          };
        } else {
          return {
            ...application,
            isChecked: selectedData.isChecked,
          };
        }
      }

      if (allSelected) {
        return {
          ...application,
          isChecked: true,
        };
      }

      // If selectedData is not found and allSelected is false, return the original application
      return application;
    });

    const filteredSelectedRowData = selectedRowData.filter(
      (data) =>
        !updatedSelectedData.some(
          (updatedData) => updatedData.applicationNumber === data.applicationNumber
        )
    );

    const mergedData = [...filteredSelectedRowData, ...updatedSelectedData];

    setApplicationListByRmId(updatedSelectedData);
    if (!checkSelectedDataLenght(mergedData)) {
      setFormikInitialValues(updateApplicationRmValues);
    }
    if (allSelected) {
      setSelectedRowData(updatedSelectedData);
      setAllSelected(false);
    } else {
      setSelectedRowData(mergedData);
    }
  };

  const handleSubmit = async (
    val: updateApplicationValues,
    { setSubmitting }: FormikHelpers<updateApplicationValues>
  ) => {
    setFetchDataLoader(true);
    try {
      setFeatChDataFromRmId(val?.featchDataFromRm.toString());
      setCurrentPage(1);
      await addTableData(val?.featchDataFromRm.toString(), 1);
      setViewTable(true);
    } catch (error) {
      console.log((error as Error).message);
    } finally {
      setSubmitting(false);
    }
    setFetchDataLoader(false);
  };
  const handleSelectAllMobileView = (val: boolean) => {
    const updatedData = applicationListByRmId.map((tableData) => {
      return { ...tableData, isChecked: val };
    });
    addSelectedRow(updatedData);
  };
  const handleSearch = (value: string) => {
    if (applicationListByRmId.length > 0 && selectedRowData.length > 0) {
      removeSelectedData();
    }
    setCurrentPage(1);
    setSearch(value);
  };
  const handleSelectAll = (selectVal: boolean): void => {
    if (allSelected) {
      removeSelectedData();
      return;
    }
    setConfirmDialogForAllSelect(selectVal);
  };
  const updateSendRmData = (rmId: number) => {
    setViewTable(false);
    setSearch('');
    setCurrentPage(1);
    removeSelectedData();
    setSelectedRowData((prevData) => prevData.map((data) => ({ ...data, isChecked: false })));
    const filteredRmsData = rmsData.filter((rms) => rms.id !== rmId);
    setSendToRmsData(filteredRmsData);
  };

  useEffect(() => {
    (async function () {
      try {
        addTableData(featChDataFromRmId, 1);
      } catch (e) {
        console.log((e as Error).message);
      }
    })();
  }, [debounceSearchString]);

  useEffect(() => {
    (async function () {
      try {
        const status = (await dispatch(
          getApplicationStatus()
        )) as unknown as IncludedApplicationStatusFile;
        setApplicationStatus(status.included_application_status);
        const res = (await dispatch(getRMsList())) as unknown as newRmType;

        const { rms } = res || {};
        setRmsData(rms);
      } catch (e) {
        console.log((e as Error).message);
      }
    })();
  }, []);
  return (
    <CommonLayout>
      <>
        <Formik
          initialValues={formikInitialValues}
          onSubmit={handleSubmit}
          validate={(values: any) => {
            try {
              validateYupSchema(values, updateApplicationRmSchema, true, values);
            } catch (e) {
              return yupToFormErrors(e);
            }
          }}
          enableReinitialize={true}>
          {({ handleSubmit, values, setFieldValue, setValues }) => {
            return (
              <>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: 18,
                      fontWeight: 500,
                      mb: { xs: 2, md: 4 },
                      p: { xs: '5px 15px', md: '' },
                      bgcolor: '#e5f1f8',
                      borderRadius: '10px',
                    }}>
                    Update Applications RM
                  </Typography>
                </Box>
                <Grid
                  onSubmit={handleSubmit}
                  container
                  rowSpacing={1}
                  columnSpacing={3}
                  component="form"
                  noValidate
                  sx={{
                    width: '100%',
                    ml: 0,
                    p: '20px',
                    bgcolor: 'common.white',
                    mt: 1.5,
                    '.MuiInputLabel-root': { whiteSpace: 'nowrap' },
                  }}
                  columns={12}>
                  <Grid
                    item
                    container
                    spacing={2}
                    xs={12}
                    sm={6}
                    sx={{
                      paddingLeft: { xs: '0', sm: '24px' },
                      '.MuiGrid-item': {
                        pl: { xs: 0, sm: '24px' },
                        pt: { xs: 0 },
                      },
                    }}>
                    <Grid item xs={12} md={7}>
                      <MFSelectField
                        disabled={updateRmLoader}
                        sx={{ paddingLeft: { xs: '0' }, marginTop: '0', marginBottom: '0' }}
                        name={`featchDataFromRm`}
                        label={'Select Rm *'}
                        items={rmsData.map((item) => ({
                          key: item.name,
                          value: item.id,
                        }))}
                        onChange={(e) => {
                          setValues({
                            sendDataToRm: '',
                            featchDataFromRm: (e.target.value as string) || '',
                          });
                          updateSendRmData(e.target.value as number);
                        }}
                        applyLabelStyles={true}
                      />
                    </Grid>
                    <Grid
                      sx={{
                        '.MuiGrid-item': {
                          pl: { xs: 0, sm: '24px' },
                          pt: { xs: 0, sm: '24px' },
                        },
                        flex: '1',
                        p: { xs: '0' },
                        width: 'fit-contet',
                        whiteSpace: 'nowrap',
                        position: 'relative',
                        top: { xs: '0.3rem', sm: '0.7rem' },
                      }}
                      item
                      xs={12}
                      md={5}>
                      <MFSubmitButton
                        disabled={updateRmLoader}
                        loadingEnable={featchDataLoader}
                        label="Fetch Data"
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    sx={{
                      paddingRight: { xs: '0', sm: '24px' },
                      paddingLeft: { xs: '0', sm: '24px' },
                      '.MuiGrid-item': {
                        pl: { xs: 0, sm: '24px' },
                        pt: { xs: 0 },
                      },
                    }}
                    item
                    container
                    spacing={2}
                    xs={12}
                    sm={6}>
                    <Grid sx={{ height: 'max-content' }} item xs={12} md={7}>
                      <MFSelectField
                        sx={{ marginTop: '0', marginBottom: '0' }}
                        disabled={
                          !(selectedRowData.some((val) => val.isChecked) || allSelected) ||
                          updateRmLoader
                        }
                        name={`sendDataToRm`}
                        label={'Update to new RM *'}
                        items={sendToRmData.map((item) => ({
                          key: item.name,
                          value: item.id,
                        }))}
                        onChange={(e) => {
                          setFieldValue('sendDataToRm', e.target.value);
                        }}
                        applyLabelStyles={true}
                      />
                    </Grid>
                    <Grid
                      sx={{
                        position: 'relative',
                        top: { xs: '0.3rem', sm: '0.7rem' },
                      }}
                      item
                      xs={12}
                      md={5}>
                      <LoadingButton
                        loadingPosition="start"
                        loading={updateRmLoader}
                        onClick={() => setConfirmDialog(true)}
                        variant="contained"
                        fullWidth
                        sx={{
                          lineHeight: 1.5,
                          fontSize: 14,
                          mt: 3,
                          mb: 2,
                        }}
                        disabled={
                          !(
                            (checkSelectedDataLenght(selectedRowData) || allSelected) &&
                            !!values.sendDataToRm
                          )
                        }>
                        Update RM
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </Grid>

                {viewTable && (
                  <>
                    <Grid
                      container
                      sx={{
                        p: '20px 30px',
                        pb: 0,
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        bgcolor: 'common.white',
                        mb: 1.5,
                        mt: 1.5,
                      }}>
                      <Grid
                        item
                        container
                        sx={{ width: 'unset', alignItems: 'flex-end', gap: 2, pb: 3 }}>
                        <Grid item>
                          <FormControl
                            disabled={updateRmLoader}
                            variant="standard"
                            sx={{ display: 'block' }}>
                            <InputLabel
                              htmlFor="input-with-icon-adornment"
                              sx={{ '&.Mui-focused': { color: 'text.primary' } }}>
                              Search Application
                            </InputLabel>
                            <Input
                              id="input-with-icon-adornment"
                              value={search}
                              onChange={({ target: { value } }) => {
                                handleSearch(value);
                              }}
                              endAdornment={
                                <InputAdornment position="end">
                                  <IconButton>
                                    <SearchSharpIcon sx={{ color: 'primary.main' }} />
                                  </IconButton>
                                </InputAdornment>
                              }
                            />
                          </FormControl>
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        container
                        sx={{ width: 'unset', alignItems: 'flex-end', gap: 2, pb: 3 }}>
                        <Grid item>
                          <Button
                            onClick={() => handleSelectAll(!confirmDialogForAllSelect)}
                            disabled={!applicationListByRmId?.length || updateRmLoader}
                            variant="outlined"
                            startIcon={<Checkbox checked={allSelected} />}
                            sx={{
                              color: 'primary.main',
                              fontWeight: 500,
                              fontSize: 14,
                              mr: 2.5,
                              p: 1,
                              pt: 0,
                              pb: 0,
                            }}>
                            Select All
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                    {applicationListByRmId.length > 1 && (
                      <Hidden smUp={true}>
                        <Box>
                          Select All Cards
                          <Checkbox
                            checked={
                              checkSelectedDataLenght(applicationListByRmId) ===
                                applicationListByRmId.length || allSelected
                            }
                            onChange={(e) => handleSelectAllMobileView(e.target.checked)}
                          />
                        </Box>
                      </Hidden>
                    )}
                    <DashboardAndApplicationsList
                      addSelectedRowData={addSelectedRow}
                      disableCheckBox={allSelected}
                      addCheckboxHeader={true}
                      isLoading={loader}
                      applicationsList={
                        applicationListByRmId.length > 0 ? applicationListByRmId : []
                      }
                      boxStyles={{ minWidth: 650 }}
                    />
                    {!!applicationListByRmId.length && (
                      <Stack direction="row" justifyContent="center" sx={{ mt: 5 }}>
                        <Pagination
                          count={noOfPages}
                          page={currentPage}
                          color="primary"
                          onChange={handlePageChange}
                        />
                      </Stack>
                    )}
                    <ConfirmationDialog
                      message={`Are you sure you want to update the applications ?`}
                      open={confirmDialog}
                      setOpen={() => setConfirmDialog(false)}
                      onSave={async () => {
                        setConfirmDialog(false);
                        try {
                          setUpdateRmLoader(true);
                          await dispatch(
                            bulkMakersChange({
                              applications: selectedRowData
                                .filter((row) => row.isChecked)
                                .map((row) => row.applicationNumber),
                              rm_id: values.sendDataToRm,
                              current_rm_id: values.featchDataFromRm,
                              selectAll: allSelected,
                            })
                          );
                          enqueueSnackbar('Applications RM Updated Successfully.', {
                            variant: 'success',
                            autoHideDuration: 3000,
                          });
                          setCurrentPage(1);
                          addTableData(featChDataFromRmId, 1);
                          removeSelectedData();
                          setSelectedRowData([]);
                          setAllSelected(false);
                          setFieldValue('sendDataToRm', '');
                          setSearch('');
                        } catch (e) {
                          console.error((e as Error).message);
                        }
                        setUpdateRmLoader(false);
                      }}
                      onCancel={() => setConfirmDialog(false)}
                    />
                    <ConfirmationDialog
                      message={`Are you sure you want to select all applications ?`}
                      open={confirmDialogForAllSelect}
                      setOpen={() => setConfirmDialogForAllSelect(false)}
                      onSave={async () => {
                        setAllSelected(true);
                        setConfirmDialogForAllSelect(false);
                      }}
                      onCancel={() => setConfirmDialogForAllSelect(false)}
                    />
                  </>
                )}
              </>
            );
          }}
        </Formik>
      </>
    </CommonLayout>
  );
}
